// Enhanced Speech Service for Text-to-Speech and Audio Playback
class SpeechService {
  constructor() {
    this.synthesis = window.speechSynthesis;
    this.voices = [];
    this.loadVoices();
  }

  loadVoices() {
    // Load available voices
    const updateVoices = () => {
      this.voices = this.synthesis.getVoices();
    };

    updateVoices();
    
    // Chrome loads voices asynchronously
    if (this.synthesis.onvoiceschanged !== undefined) {
      this.synthesis.onvoiceschanged = updateVoices;
    }
  }

  getVoiceForLanguage(lang = 'en') {
    // Find the best voice for the language
    const langVoices = this.voices.filter(voice => 
      voice.lang.toLowerCase().startsWith(lang.toLowerCase())
    );

    if (langVoices.length > 0) {
      // Prefer native voices
      const nativeVoice = langVoices.find(voice => voice.localService);
      return nativeVoice || langVoices[0];
    }

    // Fallback to default voice
    return this.voices[0];
  }

  speak(text, options = {}) {
    return new Promise((resolve, reject) => {
      if (!this.synthesis) {
        reject(new Error('Speech synthesis not supported'));
        return;
      }

      // Stop any current speech
      this.synthesis.cancel();

      const utterance = new SpeechSynthesisUtterance(text);
      
      // Set options
      utterance.rate = options.rate || 0.9;
      utterance.pitch = options.pitch || 1;
      utterance.volume = options.volume || 1;

      // Set voice based on language
      if (options.lang) {
        const voice = this.getVoiceForLanguage(options.lang);
        if (voice) {
          utterance.voice = voice;
        }
      }

      // Event handlers
      utterance.onend = () => resolve();
      utterance.onerror = (event) => reject(new Error(`Speech error: ${event.error}`));

      // Speak
      this.synthesis.speak(utterance);
    });
  }

  async playAudioUrl(audioUrl) {
    return new Promise((resolve, reject) => {
      if (!audioUrl) {
        reject(new Error('No audio URL provided'));
        return;
      }

      const audio = new Audio(audioUrl);
      
      // Set up event listeners
      audio.onended = () => resolve();
      audio.onerror = () => {
        // Fallback to text-to-speech if audio fails
        console.warn('Audio playback failed, falling back to TTS');
        reject(new Error('Audio playback failed'));
      };

      // Play audio
      audio.play().catch(error => {
        console.warn('Audio play failed:', error);
        reject(error);
      });
    });
  }

  async speakWord(word, audioUrl = null, language = 'en') {
    try {
      // First try to play the audio URL if available
      if (audioUrl) {
        await this.playAudioUrl(audioUrl);
        return { method: 'audio', success: true };
      }
    } catch (error) {
      console.warn('Audio playback failed, falling back to TTS:', error);
    }

    try {
      // Fallback to text-to-speech
      await this.speak(word, { lang: language, rate: 0.8 });
      return { method: 'tts', success: true };
    } catch (error) {
      console.error('TTS failed:', error);
      throw new Error('Unable to play pronunciation');
    }
  }

  stop() {
    if (this.synthesis) {
      this.synthesis.cancel();
    }
  }

  isSupported() {
    return 'speechSynthesis' in window;
  }

  getAvailableVoices() {
    return this.voices.map(voice => ({
      name: voice.name,
      lang: voice.lang,
      localService: voice.localService,
      default: voice.default
    }));
  }
}

// Create singleton instance
const speechService = new SpeechService();

export { speechService, SpeechService };